import * as Sentry from '@sentry/browser';

import { EndpointError } from '@iconik-io/iconik-api/src/client/response';
import { httpPutAclByObjectType } from '@iconik-io/iconik-api/src/services/acls';
import { httpPostAssetsByAssetIdViews } from '@iconik-io/iconik-api/src/services/assets';
import {
  httpDeleteAssetsByAssetIdProxiesByProxyId,
  httpGetAssetsByAssetIdProxies,
  httpPatchAssetsByAssetIdFormatsByFormatId,
} from '@iconik-io/iconik-api/src/services/files';
import { OBJECT_TYPE_ASSETS } from '@iconik-io/iconik-api/src/types/object_types';
import { getSentryFingerprint } from '@iconik-io/shared-lib/src/error_tracking/sentry/get_sentry_fingerprint';
import { deleteNormalizedEntity } from '@iconik-io/shared-lib/src/redux/entities/slice';
import {
  SENTRY_TAG_FEATURE,
  SENTRY_TAG_FEATURE_UPLOADER,
} from '@iconik-io/shared-lib/src/utils/constants/sentry';
import { timeoutPromise } from '@iconik-io/shared-lib/src/utils/timeout_promise';
import type { UploaderState } from '@iconik-io/uploader/src/state';

import { UPLOADS_PANEL } from '../../../constants/data';
import { getCurrentRouteParams } from '../../../router/current_route_params';
import type { AppDispatch, GetState } from '../../../store/types';
import type { EntityGroupAcl, EntityUserAcl } from '../../common_acls/types';
import { deleteKeyframe } from '../../entity_keyframes/thunks';
import {
  getAssetFilesetsForFilesPanel,
  getAssetProxy,
} from '../../files/thunks';
import { historyPush } from '../../history/actions';
import { errorMessage, successMessage } from '../../messages/thunks';
import { putMetadataValues } from '../../metadata_values/thunks';
import { openPanel } from '../../panels/slice';
import { selectIsLatestVersion } from '../../version/selectors';
import { getFileComponents } from '../file_components';
import { getUploadErrorMessage } from '../helpers';

const COMPONENTS_TIMEOUT_MS = 10_000;

/**
 * Whether the upload goes through what web calls uploading files to an asset:
 * new assets, versions, custom formats and placeholders. Proxies and subtitles
 * skip the asset views and upload metadata.
 */
export const isAssetFileUpload = (state: UploaderState): boolean =>
  state.uploadKind.kind === 'file' && !state.uploadKind.subtitles;

export const applyUploadAcls =
  (stateId: string, assetId: string) =>
  (dispatch: AppDispatch, getState: GetState): void => {
    const acls = getState().uploaderAcls[stateId];
    if (!acls) {
      return;
    }
    const entities: (EntityGroupAcl | EntityUserAcl)[] = [
      ...acls.users,
      ...acls.groups,
    ];
    const permissionsGroups = Object.groupBy(entities, (entity) =>
      entity.permissions.join(','),
    );
    for (const [permissions, group = []] of Object.entries(permissionsGroups)) {
      dispatch(
        httpPutAclByObjectType('assets', {
          group_ids: group.flatMap((item) =>
            'group_id' in item ? [item.group_id] : [],
          ),
          object_keys: [assetId],
          permissions: permissions.split(','),
          user_ids: group.flatMap((item) =>
            'user_id' in item ? [item.user_id] : [],
          ),
        }),
      );
    }
  };

export const applyUploadMetadata =
  (stateId: string, assetId: string) =>
  (dispatch: AppDispatch, getState: GetState): void => {
    dispatch(httpPostAssetsByAssetIdViews(assetId));
    const metadata = getState().uploaderMetadata[stateId];
    if (metadata?.view && metadata.fields) {
      dispatch(putMetadataValues(assetId, metadata.view, metadata.fields));
    }
  };

/**
 * The media info read from the file is attached once the format exists, rather
 * than holding up its creation the way the legacy engine did.
 */
export const applyFormatComponents =
  (state: UploaderState, assetId: string, formatId: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    const { streamSource } = state.fileInfo;
    const componentsPromise =
      streamSource.type === 'fileRef'
        ? getFileComponents(streamSource.id)
        : undefined;
    if (!componentsPromise) {
      return;
    }
    const components = await timeoutPromise(
      componentsPromise,
      COMPONENTS_TIMEOUT_MS,
    );
    if (!components?.length) {
      return;
    }
    await dispatch(
      httpPatchAssetsByAssetIdFormatsByFormatId(assetId, formatId, {
        components,
      }),
    );
  };

/**
 * iconik keeps every proxy uploaded for an asset, and an earlier failure can
 * leave a second one behind, so everything but the new proxy goes. Its
 * keyframes go too, since the new proxy generates its own.
 */
export const replaceAssetProxy =
  (assetId: string, proxyId: string) =>
  async (dispatch: AppDispatch, getState: GetState): Promise<void> => {
    const proxies = await dispatch(
      httpGetAssetsByAssetIdProxies(assetId, { per_page: 50 }),
    );
    const otherProxies = proxies.body.objects.filter(
      (object) => object.id !== proxyId,
    );
    await Promise.all(
      otherProxies.map(({ id }) =>
        dispatch(httpDeleteAssetsByAssetIdProxiesByProxyId(assetId, id)),
      ),
    );
    for (const { id } of otherProxies) {
      dispatch(deleteNormalizedEntity({ key: 'proxies', id }));
    }

    const keyframeIds =
      getState().entityKeyframes.keyframesIdsByEntityId[assetId] ?? [];
    await Promise.all(
      keyframeIds.map((keyframeId) =>
        dispatch(
          deleteKeyframe({
            entity: OBJECT_TYPE_ASSETS,
            entityId: assetId,
            keyframeId,
          }),
        ),
      ),
    );

    if (
      getState().pages.asset.assetId === assetId &&
      selectIsLatestVersion(
        getState(),
        assetId,
        getCurrentRouteParams().versionId,
      )
    ) {
      dispatch(getAssetProxy(assetId));
      dispatch(getAssetFilesetsForFilesPanel(assetId));
    }
  };

/** Takes the user to a version as soon as the upload has created it. */
export const showCreatedVersion =
  (state: UploaderState) =>
  (dispatch: AppDispatch): void => {
    const target = state.target;
    if (target.type === 'newVersion' && target.versionId) {
      dispatch(historyPush(`/asset/${target.assetId}/v/${target.versionId}`));
    }
  };

export const handleUploadCompleted =
  (state: UploaderState) =>
  async (dispatch: AppDispatch): Promise<void> => {
    const { target, uploadKind } = state;
    if (uploadKind.kind === 'proxy' && uploadKind.proxyId && target.assetId) {
      await dispatch(replaceAssetProxy(target.assetId, uploadKind.proxyId));
      return;
    }
    if (
      uploadKind.kind === 'file' &&
      uploadKind.subtitles?.createTranscription
    ) {
      dispatch(successMessage({ text: 'Transcribe job scheduled' }));
    }
  };

/**
 * What a failed upload was adding to an asset that already existed, which is
 * what the user is told about. A failed new asset shows in the uploads panel
 * alone.
 */
export const getFailedUploadEntityName = (
  state: UploaderState,
): string | undefined => {
  const { target, uploadKind } = state;
  if (uploadKind.kind === 'proxy') {
    return 'proxy';
  }
  if (uploadKind.subtitles) {
    return 'subtitles';
  }
  if (target.type === 'newVersion') {
    return 'version';
  }
  if (target.type === 'existingAsset' && target.fillsPlaceholder) {
    return 'placeholder';
  }
  if (uploadKind.formatName) {
    return 'format';
  }
  return undefined;
};

export const notifyUploadFailed =
  (state: UploaderState, fileExists: boolean) =>
  (dispatch: AppDispatch): void => {
    const entityName = getFailedUploadEntityName(state);
    if (!entityName) {
      return;
    }
    dispatch(openPanel(UPLOADS_PANEL));
    dispatch(
      errorMessage({ text: getUploadErrorMessage(entityName, fileExists) }),
    );
  };

const findEndpointError = (error: unknown): EndpointError | undefined => {
  let current = error;
  while (current instanceof Error) {
    if (current instanceof EndpointError) {
      return current;
    }
    current = current.cause;
  }
  return undefined;
};

/**
 * Groups upload failures by the request that failed, when there is one, so
 * that a single broken endpoint doesn't scatter across Sentry issues.
 */
export const reportUploadError = (error: Error): void => {
  Sentry.withScope((scope) => {
    scope.setTag(SENTRY_TAG_FEATURE, SENTRY_TAG_FEATURE_UPLOADER);
    const endpointError = findEndpointError(error);
    if (endpointError) {
      scope.setExtra(
        'requestHeaders',
        JSON.stringify(Object.keys(endpointError.request.headers ?? {})),
      );
    }
    scope.setFingerprint(
      getSentryFingerprint(endpointError ?? error, {
        tag: SENTRY_TAG_FEATURE_UPLOADER,
      }),
    );
    Sentry.captureException(error);
  });
};
